import React, { useState, useEffect, useRef } from 'react';
import { Image, Plus, Edit2, Trash2, Eye, EyeOff, ExternalLink, Code, RefreshCw, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIONS = [
  { value: 'header', label: 'Header Banner' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'between_content', label: 'Between Content' },
  { value: 'footer', label: 'Footer' },
  { value: 'popup', label: 'Popup' }
];

const BannerManager = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({ total_impressions: 0, total_clicks: 0 });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    ad_type: 'image',
    image_url: '',
    link_url: '',
    alt_text: '',
    embed_code: '',
    position: 'sidebar',
    priority: 0,
    start_date: '',
    end_date: '',
    show_on_pages: ['all']
  });
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please use JPG, PNG, or GIF");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/uploads/banner`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Set the full URL for the uploaded image
      const imageUrl = `${BACKEND_URL}${response.data.file.url}`;
      setFormData({ ...formData, image_url: imageUrl });
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/ads/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAds(response.data.ads || []);
      setStats(response.data.stats || { total_impressions: 0, total_clicks: 0 });
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      ad_type: 'image',
      image_url: '',
      link_url: '',
      alt_text: '',
      embed_code: '',
      position: 'sidebar',
      priority: 0,
      start_date: '',
      end_date: '',
      show_on_pages: ['all']
    });
    setEditingAd(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      // Prepare data based on ad type
      const submitData = { ...formData };
      if (formData.ad_type === 'image') {
        delete submitData.embed_code;
      } else {
        delete submitData.image_url;
        delete submitData.link_url;
        delete submitData.alt_text;
      }
      
      if (editingAd) {
        await axios.put(`${API}/ads/admin/${editingAd.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/ads/admin/create`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchAds();
    } catch (error) {
      console.error('Error saving ad:', error);
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title || '',
      ad_type: ad.ad_type || 'image',
      image_url: ad.image_url || '',
      link_url: ad.link_url || '',
      alt_text: ad.alt_text || '',
      embed_code: ad.embed_code || '',
      position: ad.position || 'sidebar',
      priority: ad.priority || 0,
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
      show_on_pages: ad.show_on_pages || ['all']
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/ads/admin/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAds();
    } catch (error) {
      console.error('Error deleting ad:', error);
    }
  };

  const handleToggle = async (adId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/ads/admin/${adId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAds();
    } catch (error) {
      console.error('Error toggling ad:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      paused: 'bg-yellow-500/20 text-yellow-400',
      scheduled: 'bg-blue-500/20 text-blue-400',
      expired: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {status}
      </span>
    );
  };

  const getTypeBadge = (adType) => {
    if (adType === 'code') {
      return (
        <span className="px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-400 flex items-center gap-1">
          <Code className="h-3 w-3" />
          Embed
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400 flex items-center gap-1">
        <Image className="h-3 w-3" />
        Image
      </span>
    );
  };

  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Image className="h-5 w-5 text-orange-400" />
            Banner Ad Management
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAds} className="border-gray-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="h-4 w-4 mr-2" />
                  New Ad
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingAd ? 'Edit Banner Ad' : 'Create New Banner Ad'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Title and Position */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="Ad campaign name"
                        className="bg-gray-800 border-gray-700"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Position *</Label>
                      <Select value={formData.position} onValueChange={(v) => setFormData({...formData, position: v})}>
                        <SelectTrigger className="bg-gray-800 border-gray-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {POSITIONS.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Ad Type Tabs */}
                  <div className="space-y-2">
                    <Label>Ad Type</Label>
                    <Tabs value={formData.ad_type} onValueChange={(v) => setFormData({...formData, ad_type: v})}>
                      <TabsList className="grid w-full grid-cols-2 bg-gray-800">
                        <TabsTrigger value="image" className="data-[state=active]:bg-orange-500">
                          <Image className="h-4 w-4 mr-2" />
                          Image Ad
                        </TabsTrigger>
                        <TabsTrigger value="code" className="data-[state=active]:bg-orange-500">
                          <Code className="h-4 w-4 mr-2" />
                          Google/Embed Code
                        </TabsTrigger>
                      </TabsList>
                      
                      {/* Image Ad Fields */}
                      <TabsContent value="image" className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Banner Image *</Label>
                          <div className="space-y-3">
                            {/* Upload Button */}
                            <div className="flex items-center gap-3">
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept=".jpg,.jpeg,.png,.gif"
                                className="hidden"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                className="border-gray-700 text-gray-300"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                              >
                                <Upload className="mr-2 h-4 w-4" />
                                {uploading ? 'Uploading...' : 'Upload Image'}
                              </Button>
                              <span className="text-xs text-gray-500">JPG, PNG, GIF (max 5MB) • Auto-optimized to 1200×400px</span>
                            </div>
                            
                            {/* Or Enter URL */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">or enter URL:</span>
                            </div>
                            <Input
                              value={formData.image_url}
                              onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                              placeholder="https://example.com/banner.jpg"
                              className="bg-gray-800 border-gray-700"
                            />
                            
                            {/* Image Preview */}
                            {formData.image_url && (
                              <div className="relative inline-block">
                                <img 
                                  src={formData.image_url} 
                                  alt="Preview" 
                                  className="mt-2 max-h-32 rounded border border-gray-700"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, image_url: ''})}
                                  className="absolute top-0 right-0 bg-red-500 rounded-full p-1 hover:bg-red-600"
                                >
                                  <X className="h-3 w-3 text-white" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Click URL *</Label>
                          <Input
                            value={formData.link_url}
                            onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                            placeholder="https://advertiser.com/landing-page"
                            className="bg-gray-800 border-gray-700"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Alt Text</Label>
                          <Input
                            value={formData.alt_text}
                            onChange={(e) => setFormData({...formData, alt_text: e.target.value})}
                            placeholder="Describe the ad for accessibility"
                            className="bg-gray-800 border-gray-700"
                          />
                        </div>
                      </TabsContent>
                      
                      {/* Embed Code Fields */}
                      <TabsContent value="code" className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Embed Code (Google AdSense, etc.) *</Label>
                          <Textarea
                            value={formData.embed_code}
                            onChange={(e) => setFormData({...formData, embed_code: e.target.value})}
                            placeholder={`Paste your Google AdSense or other ad code here...

Example:
<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>
<ins class="adsbygoogle"
     style="display:block"
     data-ad-client="ca-pub-XXXXXXX"
     data-ad-slot="XXXXXXX"
     data-ad-format="auto"></ins>
<script>(adsbygoogle = window.adsbygoogle || []).push({});</script>`}
                            className="bg-gray-800 border-gray-700 min-h-[200px] font-mono text-sm"
                          />
                          <p className="text-xs text-gray-500">
                            Supports Google AdSense, Media.net, or any HTML/JS ad embed code
                          </p>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                  
                  {/* Priority and Dates */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Input
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
                        className="bg-gray-800 border-gray-700"
                      />
                      <p className="text-xs text-gray-500">Higher = shown first</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-gray-700">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                      {editingAd ? 'Update Ad' : 'Create Ad'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Image className="h-4 w-4" />
              Total Ads
            </div>
            <div className="text-2xl font-bold text-white">{ads.length}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Eye className="h-4 w-4" />
              Total Impressions
            </div>
            <div className="text-2xl font-bold text-white">{stats.total_impressions.toLocaleString()}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <ExternalLink className="h-4 w-4" />
              Total Clicks
            </div>
            <div className="text-2xl font-bold text-white">{stats.total_clicks.toLocaleString()}</div>
          </div>
        </div>

        {/* Ads List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No banner ads yet. Create your first ad!</p>
            <p className="text-sm mt-2">Supports custom images or Google AdSense embed codes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ads.map((ad) => (
              <div 
                key={ad.id} 
                className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex items-center gap-4"
              >
                {/* Preview */}
                <div className="w-32 h-20 bg-gray-700 rounded overflow-hidden flex-shrink-0">
                  {ad.ad_type === 'code' ? (
                    <div className="w-full h-full flex items-center justify-center text-purple-400 bg-purple-500/10">
                      <Code className="h-8 w-8" />
                    </div>
                  ) : ad.image_url ? (
                    <img src={ad.image_url} alt={ad.alt_text || ad.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <Image className="h-8 w-8" />
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="text-white font-medium truncate">{ad.title}</h4>
                    {getTypeBadge(ad.ad_type)}
                    {getStatusBadge(ad.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="capitalize">{ad.position?.replace('_', ' ')}</span>
                    <span>Priority: {ad.priority}</span>
                    {ad.ad_type === 'image' && ad.link_url && (
                      <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-orange-400 hover:text-orange-300">
                        <ExternalLink className="h-3 w-3" />
                        Link
                      </a>
                    )}
                    {ad.ad_type === 'code' && (
                      <span className="text-purple-400">Google/Embed</span>
                    )}
                  </div>
                </div>
                
                {/* Stats */}
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <div className="text-gray-400">Impressions</div>
                    <div className="text-white font-medium">{ad.impressions?.toLocaleString() || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-400">Clicks</div>
                    <div className="text-white font-medium">{ad.clicks?.toLocaleString() || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-400">CTR</div>
                    <div className="text-white font-medium">{ad.ctr || 0}%</div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToggle(ad.id)}
                    className={ad.status === 'active' ? 'text-green-400 hover:text-green-300' : 'text-gray-400 hover:text-white'}
                    title={ad.status === 'active' ? 'Pause' : 'Activate'}
                  >
                    {ad.status === 'active' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(ad)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(ad.id)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BannerManager;
